import React, { useState, useEffect } from 'react';
import { SdkConfiguration } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import Toast from '../components/ui/Toast';
import { AlertTriangle } from 'lucide-react';
import { getSDKUrl, API_BASE_URL } from '../config';

const defaultConfig: SdkConfiguration = {
    primaryColor: '#6366F1',
    secondaryColor: '#1F2937',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    userMessageTextColor: '#ffffff',
    botMessageTextColor: '#ffffff',
    botName: 'Mentoros',
    hoverColor: '#ff8c00',
    welcomeMessage: "Hello! I'm your shopping assistant. I can help you browse products and navigate to checkout. Try asking me to help you checkout!",
};

// Helper function to escape HTML in user-provided content
const escapeHtml = (text: string) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Helper function to escape strings for use inside JavaScript string literals in srcdoc
const escapeForJS = (text: string) => {
  return text
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/'/g, "\\'")    // Escape single quotes
    .replace(/"/g, '\\"')    // Escape double quotes
    .replace(/\n/g, '\\n')   // Escape newlines
    .replace(/\r/g, '\\r');  // Escape carriage returns
};

// Helper function to generate the live chatbot HTML content for the iframe
const getLiveChatbotContent = (config: SdkConfiguration) => `
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=${config.fontFamily.split(',')[0].replace(/\s/g, '+')}:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      /* AI Chatbot Widget Styles - Embedded Version */
      :root {
        --ai-chatbot-primary-color: ${config.primaryColor};
        --ai-chatbot-secondary-color: ${config.secondaryColor};
        --ai-chatbot-hover-color: ${config.hoverColor || '#ff8c00'};
        --ai-chatbot-user-message-bg: ${config.secondaryColor};
        --ai-chatbot-user-message-text: ${config.userMessageTextColor || '#ffffff'};
        --ai-chatbot-ai-message-bg: ${config.primaryColor};
        --ai-chatbot-ai-message-text: ${config.botMessageTextColor || '#ffffff'};
        --ai-chatbot-header-bg: ${config.primaryColor};
        --ai-chatbot-header-text: #ffffff;
        --ai-chatbot-window-bg-start: #FFFFFF;
        --ai-chatbot-window-bg-end: #F9FAFB;
        --ai-chatbot-messages-bg-start: #FFFFFF;
        --ai-chatbot-messages-bg-end: #F9FAFB;
        --ai-chatbot-input-bg: #ffffff;
        --ai-chatbot-input-border: #d1d5db;
        --ai-chatbot-input-focus-border: ${config.primaryColor};
        --ai-chatbot-send-button-bg: ${config.primaryColor};
        --ai-chatbot-send-button-text: #ffffff;
        --ai-chatbot-typing-indicator-bg: ${config.primaryColor}1A;
        --ai-chatbot-typing-dot-color: #6b7280;
        --ai-chatbot-error-message-bg: #fee2e2;
        --ai-chatbot-error-message-text: #dc2626;
        --ai-chatbot-shadow-color: rgba(0, 0, 0, 0.15);
        --ai-chatbot-border-animation-color-1: ${config.primaryColor}40;
        --ai-chatbot-border-animation-color-2: ${config.primaryColor};
        --ai-chatbot-border-animation-color-3: ${config.primaryColor}CC;
        --ai-chatbot-font-family: ${config.fontFamily};
        --ai-chatbot-font-size: ${config.fontSize};
      }
      
      /* Chatbot Container */
      #ai-chatbot-widget {
        position: fixed;
        bottom: 0;
        right: 0;
        z-index: 9999;
        font-family: var(--ai-chatbot-font-family);
      }
      
      /* Chat Button */
      .ai-chat-button {
        position: fixed;
        bottom: 1.5rem;
        right: 1.5rem;
        width: 4rem;
        height: 4rem;
        background-color: var(--ai-chatbot-primary-color);
        color: white;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 10px 25px var(--ai-chatbot-shadow-color);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.35s cubic-bezier(0.25, 0.1, 0.25, 1);
        overflow: visible;
        isolation: isolate;
      }

      /* Security indicator for chat button */
      .ai-chat-button .ai-security-indicator {
        position: absolute;
        top: 2px;
        right: 2px;
        font-size: 10px;
        opacity: 0.8;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }

      /* Moving border container */
      .ai-chat-button::before {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 50%;
        padding: 3px;
        background: conic-gradient(
          from 0deg,
          transparent 10deg,
          var(--ai-chatbot-border-animation-color-1) 25deg,
          var(--ai-chatbot-border-animation-color-2) 40deg,
          var(--ai-chatbot-border-animation-color-3) 55deg,
          transparent 70deg,
          transparent 110deg,
          var(--ai-chatbot-border-animation-color-1) 125deg,
          var(--ai-chatbot-border-animation-color-2) 140deg,
          var(--ai-chatbot-border-animation-color-3) 155deg,
          transparent 170deg,
          transparent 230deg,
          var(--ai-chatbot-border-animation-color-1) 245deg,
          var(--ai-chatbot-border-animation-color-2) 260deg,
          var(--ai-chatbot-border-animation-color-3) 275deg,
          transparent 290deg,
          transparent 360deg
        );
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: xor;
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        animation: movingBorder 3s linear infinite;
        opacity: 0;
        transition: opacity 0.5s ease;
        z-index: -1;
      }

      .ai-chat-button:hover::before,
      .ai-chat-button.border-active::before {
        opacity: 1;
      }

      /* Enhanced moving border on activity */
      .ai-chat-button.pulse-border::before {
        opacity: 1;
        animation: movingBorderPulse 2s ease-in-out;
      }

      @keyframes movingBorder {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes movingBorderPulse {
        0% {
          opacity: 0;
          transform: rotate(0deg) scale(0.8);
        }
        50% {
          opacity: 1;
          transform: rotate(180deg) scale(1.1);
        }
        100% {
          opacity: 0.7;
          transform: rotate(360deg) scale(1);
        }
      }
      
      .ai-chat-button:hover {
        transform: scale(1.1);
        box-shadow: 0 15px 35px var(--ai-chatbot-shadow-color);
        background-color: var(--ai-chatbot-primary-color);
      }

      .ai-chat-button:active {
        transform: scale(0.95);
      }
      
      .ai-chat-button svg {
        width: 2rem;
        height: 2rem;
        transition: all 0.35s cubic-bezier(0.25, 0.1, 0.25, 1);
      }
      
      .ai-chat-button.active svg.chat-icon {
        transform: rotate(90deg) scale(0);
        opacity: 0;
      }
      
      .ai-chat-button svg.close-icon {
        position: absolute;
        transform: rotate(-90deg) scale(0);
        opacity: 0;
      }
      
      .ai-chat-button.active svg.close-icon {
        transform: rotate(0) scale(1);
        opacity: 1;
      }
      
      /* Chat Window */
      .ai-chat-window {
        position: fixed;
        bottom: 6rem;
        right: 1.5rem;
        width: min(90vw, 400px);
        height: min(70vh, 600px);
        background: linear-gradient(180deg, var(--ai-chatbot-window-bg-start) 0%, var(--ai-chatbot-window-bg-end) 100%);
        border-radius: 1rem;
        box-shadow: 0 20px 60px var(--ai-chatbot-shadow-color);
        display: flex;
        flex-direction: column;
        opacity: 0;
        transform: scale(0.8) translateY(20px);
        transform-origin: bottom right;
        pointer-events: none;
        transition: all 0.15s cubic-bezier(0.05, 0.1, 0.05, 1);
      }
      
      .ai-chat-window.active {
        opacity: 1;
        transform: scale(1) translateY(0);
        pointer-events: all;
      }
      
      /* Chat Header */
      .ai-chat-header {
        background-color: var(--ai-chatbot-header-bg);
        color: var(--ai-chatbot-header-text);
        padding: 1rem;
        border-radius: 1rem 1rem 0 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      }

      .ai-chat-header-left {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        min-width: 0;
      }

      .ai-chat-logo {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .ai-chat-logo svg {
        width: 100%;
        height: 100%;
      }

      .ai-chat-logo img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      
      .ai-chat-header h3 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .ai-chat-header > div:last-child {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        align-items: center;
        flex-shrink: 0;
      }

      .ai-chat-header button {
        background: transparent;
        border: none;
        color: var(--ai-chatbot-header-text);
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 50%;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .ai-chat-header button:hover {
        background-color: rgba(255, 255, 255, 0.2);
        transform: scale(1.05);
      }

      .ai-chat-header button:active {
        transform: scale(0.95);
      }

      .ai-reset-button {
        position: relative;
      }

      .ai-reset-button:hover {
        background-color: rgba(255, 255, 255, 0.15);
      }

      .ai-reset-button svg {
        width: 1.5rem;
        height: 1.5rem;
        transition: transform 0.3s ease;
      }

      .ai-reset-button:hover svg {
        transform: rotate(360deg);
      }

      .ai-close-button svg {
        width: 1.5rem;
        height: 1.5rem;
      }
      
      .ai-encrypted-badge {
        font-size: 11px;
        opacity: 0.8;
        color: #10b981;
        display: flex;
        align-items: center;
        gap: 4px;
        margin-top: 2px;
      }
      
      /* Messages Container */
      .ai-chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
        background: linear-gradient(180deg, var(--ai-chatbot-messages-bg-start) 0%, var(--ai-chatbot-messages-bg-end) 100%);
      }
      
      /* Message Styles */
      .ai-message {
        margin-bottom: 1rem;
        display: flex;
        animation: messageSlide 0.3s ease-out;
      }
      
      @keyframes messageSlide {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .ai-message.user {
        justify-content: flex-end;
      }
      
      .ai-message-content {
        max-width: 85%;
        padding: 0.75rem 1rem;
        border-radius: 1.125rem;
        font-size: var(--ai-chatbot-font-size);
        line-height: 1.5;
        word-wrap: break-word;
        background-color: transparent;
        color: inherit;
      }
      
      .ai-message.user .ai-message-content {
        background-color: var(--ai-chatbot-user-message-bg) !important;
        color: var(--ai-chatbot-user-message-text) !important;
        border-bottom-right-radius: 0.25rem;
      }
      
      .ai-message.model .ai-message-content {
        background-color: var(--ai-chatbot-ai-message-bg) !important;
        color: var(--ai-chatbot-ai-message-text) !important;
        border-bottom-left-radius: 0.25rem;
      }

      .ai-message.error .ai-message-content {
          background-color: var(--ai-chatbot-error-message-bg);
          color: var(--ai-chatbot-error-message-text);
          width: 100%;
      }

      /* Error message styling */
      .ai-message.ai-error-message {
          background: var(--ai-chatbot-error-message-bg) !important;
          color: var(--ai-chatbot-error-message-text) !important;
          border-left: 4px solid #dc2626;
          box-shadow: 0 2px 4px rgba(220, 38, 38, 0.1);
      }

      .ai-message.ai-error-message .ai-message-content {
          color: var(--ai-chatbot-error-message-text) !important;
          font-weight: 500;
      }

      /* Error state for typing indicator */
      .ai-typing-indicator.error {
          background: var(--ai-chatbot-error-message-bg);
          border-left: 4px solid #dc2626;
      }

      .ai-typing-indicator.error .ai-typing-dot {
          background: #dc2626;
      }
      
      /* Input Container */
      .ai-chat-input-container {
        padding: 1rem;
        border-top: 1px solid #e5e7eb;
        background: var(--ai-chatbot-input-bg);
        border-radius: 0 0 1rem 1rem;
      }
      
      .ai-chat-input-form {
        display: flex;
        gap: 0.5rem;
      }
      
      .ai-chat-input {
        flex: 1;
        padding: 0.75rem 1rem;
        border: 1px solid var(--ai-chatbot-input-border);
        border-radius: 9999px;
        font-size: 0.875rem;
        outline: none;
        transition: all 0.2s;
        font-family: inherit;
      }
      
      .ai-chat-input:focus {
        border-color: var(--ai-chatbot-input-focus-border);
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
      }
      
      .ai-chat-send {
        padding: 0.75rem;
        background-color: var(--ai-chatbot-send-button-bg);
        color: var(--ai-chatbot-send-button-text);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .ai-chat-send:hover:not(:disabled) {
        transform: scale(1.05);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        background-color: var(--ai-chatbot-hover-color);
      }
      
      .ai-chat-send svg {
        width: 1.5rem;
        height: 1.5rem;
      }

      /* Typing Indicator */
      .ai-typing-indicator {
        display: flex;
        gap: 0.25rem;
        padding: 0.75rem 1rem;
        margin: 0.5rem 0.75rem;
        background-color: var(--ai-chatbot-typing-indicator-bg);
        border-radius: 1.125rem;
        border-bottom-left-radius: 0.25rem;
        width: fit-content;
        position: relative;
        z-index: 1;
      }

      .ai-typing-dot {
        width: 0.5rem;
        height: 0.5rem;
        background-color: var(--ai-chatbot-typing-dot-color);
        border-radius: 50%;
        animation: typingBounce 1.4s infinite ease-in-out;
      }

      .ai-typing-dot:nth-child(1) {
        animation-delay: -0.32s;
      }

      .ai-typing-dot:nth-child(2) {
        animation-delay: -0.16s;
      }

      @keyframes typingBounce {
        0%, 80%, 100% {
          transform: scale(1);
          opacity: 0.5;
        }
        40% {
          transform: scale(1.3);
          opacity: 1;
        }
      }

      /* Security Features CSS */
      .ai-rate-limit-warning {
        background: #fef3c7;
        color: #d97706;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 12px;
        text-align: center;
        margin: 8px 16px;
        border: 1px solid #fcd34d;
        animation: slideDown 0.3s ease-out;
      }

      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Message validation indicators */
      .ai-message.ai-error-message .ai-message-content::before {
        content: "⚠️";
        margin-right: 6px;
      }

      /* Input validation feedback */
      .ai-input-container {
        position: relative;
      }

      .ai-char-counter {
        position: absolute;
        bottom: -18px;
        right: 0;
        font-size: 10px;
        color: #64748b;
      }

      .ai-char-counter.warning {
        color: #f59e0b;
      }

      .ai-char-counter.error {
        color: #ef4444;
      }

      /* Security status indicator */
      .ai-security-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 8px;
        background: rgba(16, 185, 129, 0.1);
        border-radius: 6px;
        margin: 8px 16px;
        font-size: 12px;
        color: #10b981;
        border: 1px solid rgba(16, 185, 129, 0.2);
      }

      .ai-security-status.warning {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
        border-color: rgba(245, 158, 11, 0.2);
      }

      .ai-security-status.error {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
        border-color: rgba(239, 68, 68, 0.2);
      }

      /* Mobile Optimizations */
      @media (max-width: 640px) {
        .ai-chat-window {
          width: 100vw;
          height: 100vh;
          bottom: 0;
          right: 0;
          border-radius: 0;
        }

        .ai-chat-header {
          border-radius: 0;
        }

        .ai-chat-input-container {
          border-radius: 0;
        }

        .ai-chat-button {
          bottom: 1rem;
          right: 1rem;
        }
      }

      /* Mobile responsive security elements */
      @media (max-width: 480px) {
        .ai-encrypted-badge {
          font-size: 10px;
        }
        
        .ai-security-status {
          margin: 4px 8px;
          padding: 3px 6px;
          font-size: 11px;
        }
        
        .ai-rate-limit-warning {
          margin: 4px 8px;
          padding: 6px 8px;
          font-size: 11px;
        }
      }

      /* Dark mode support for security elements */
      @media (prefers-color-scheme: dark) {
        .ai-rate-limit-warning {
          background: rgba(245, 158, 11, 0.2);
          border-color: rgba(245, 158, 11, 0.3);
        }
        
        .ai-security-status {
          background: rgba(16, 185, 129, 0.2);
          border-color: rgba(16, 185, 129, 0.3);
        }
        
        .ai-char-counter {
          color: #9ca3af;
        }
      }
      body { 
        background-color: transparent; 
        margin: 0; 
        font-family: ${config.fontFamily};
        height: 100vh;
        position: relative;
      }
    </style>
  </head>
  <body>

    <!-- AI Chatbot Widget -->
    <div id="ai-chatbot-widget">
      <button class="ai-chat-button" id="ai-chat-toggle">
        <svg class="chat-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-3.774-.9L3 21l1.9-6.226A8.955 8.955 0 013 12a8 8 0 018-8 8 8 0 018 8z" />
        </svg>
        <svg class="close-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div class="ai-chat-window" id="ai-chat-window">
        <div class="ai-chat-header">
          <div class="ai-chat-header-left">
            <div class="ai-chat-logo">
              <svg viewBox="140 260 320 260" fill="currentColor">
                <path d="M380.38,386.72h-9.84c-7.82-12.61-19.31-22.95-33.38-29.3-2.49-1.12-5.42-.02-6.54,2.47-1.12,2.49-.02,5.42,2.47,6.54,23.66,10.68,38.94,34.26,38.94,60.07,0,36.31-29.55,65.85-65.87,65.85h-52.38c-36.32,0-65.87-29.54-65.87-65.85s29.55-65.87,65.87-65.87h10.05c2.73,0,4.94-2.21,4.94-4.94s-2.21-4.94-4.94-4.94h-10.05c-27.19,0-51.08,14.4-64.44,35.98h-4.84v-61.9c9.04-2.22,15.77-10.39,15.77-20.11,0-11.41-9.29-20.7-20.71-20.7s-20.7,9.29-20.7,20.7c0,9.72,6.72,17.89,15.75,20.11v62.38c-12.07,2.32-21.22,12.95-21.22,25.69v27.21c0,14.43,11.74,26.18,26.17,26.18h9.79c13.37,21.57,37.25,35.97,64.44,35.97h52.38c20.15,0,39.15-7.9,53.5-22.24,4.2-4.2,7.85-8.81,10.91-13.72h9.8c14.43,0,26.18-11.74,26.18-26.18v-27.21c0-14.43-11.74-26.17-26.18-26.17h0Zm-211.63-82.01c0-5.96,4.85-10.81,10.81-10.81s10.82,4.85,10.82,10.81-4.86,10.82-10.82,10.82c-5.96,0-10.81-4.86-10.81-10.82Zm10.81,151.67c-8.97,0-16.28-7.31-16.28-16.29v-27.21c0-8.97,7.3-16.28,16.28-16.28h4.62c-3.95,9.17-6.15,19.28-6.15,29.89s2.2,20.71,6.15,29.89h-4.63Zm217.11-16.29c0,8.98-7.31,16.29-16.29,16.29h-4.62c4.03-9.33,6.16-19.45,6.16-29.89s-2.19-20.6-6.21-29.89h4.67c8.98,0,16.29,7.3,16.29,16.28v27.21h0Z"/>
                <path d="M238.23,395.03c-10.27,0-18.62,10.07-18.62,22.45s8.35,22.45,18.62,22.45,18.61-10.07,18.61-22.45-8.35-22.45-18.61-22.45h0Zm0,35.01c-4.74,0-8.74-5.75-8.74-12.56s4-12.56,8.74-12.56,8.72,5.75,8.72,12.56-3.99,12.56-8.72,12.56Z"/>
                <path d="M261.97,457.81c-1.93,1.93-1.94,5.06,0,6.99,4.81,4.82,11.21,7.47,18.01,7.47s13.2-2.65,18.01-7.47c1.93-1.93,1.93-5.06,0-6.99-1.93-1.93-5.06-1.93-6.99,0-2.94,2.95-6.85,4.57-11.01,4.57s-8.07-1.62-11.01-4.57c-1.93-1.93-5.06-1.94-6.99,0h0Z"/>
                <path d="M321.72,439.93c10.26,0,18.61-10.07,18.61-22.45s-8.35-22.45-18.61-22.45-18.62,10.07-18.62,22.45c0,12.38,8.36,22.45,18.62,22.45h0Zm0-35.01c4.73,0,8.72,5.75,8.72,12.56s-3.99,12.56-8.72,12.56-8.74-5.75-8.74-12.56,4-12.56,8.74-12.56h0Z"/>
                <path fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="11.34" d="M271.22,359.87h12.7c.21,12.42-2.34,17.67-7.08,27.43,19.16-5.52,30.91-5.77,44.12-27.43h52.64c6.43,0,11.69-5.27,11.69-11.7v-66.14c0-6.42-5.26-11.69-11.69-11.69h-102.39c-6.43,0-11.69,5.27-11.69,11.69v66.66c.28,6.2,5.42,11.19,11.69,11.19h0Z"/>
              </svg>
            </div>
            <h3>${config.botName || 'Mentoros'} Agent</h3>
          </div>
          <div>
            <button type="button" class="ai-reset-button" id="ai-chat-reset" title="Reset Conversation">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button type="button" class="ai-close-button" id="ai-chat-header-close" title="Close Chat">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div class="ai-chat-messages" id="ai-chat-messages">
          <div class="ai-message model">
            <div class="ai-message-content">
              ${escapeHtml(config.welcomeMessage || "Hello! I'm " + (config.botName || "your shopping assistant") + ". I can help you browse products and navigate to checkout. Try asking me to help you checkout!")}
            </div>
          </div>
        </div>
        
        <div class="ai-chat-input-container">
          <div class="ai-chat-input-form">
            <input type="text" class="ai-chat-input" id="ai-chat-input" placeholder="Type your message..." />
            <button type="submit" class="ai-chat-send" id="ai-chat-send">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <script src="${getSDKUrl('chatbot.min.js')}"></script>
    <script>
      // Initialize cart as global variable
      let cart = [];
      
      // Wait for DOM to be ready
      document.addEventListener('DOMContentLoaded', function() {
        // Setup basic toggle functionality for preview
        const toggleButton = document.getElementById('ai-chat-toggle');
        const chatWindow = document.getElementById('ai-chat-window');
        const headerCloseButton = document.getElementById('ai-chat-header-close');
        const resetButton = document.getElementById('ai-chat-reset');
        const chatInput = document.getElementById('ai-chat-input');
        const sendButton = document.getElementById('ai-chat-send');
        const messagesContainer = document.getElementById('ai-chat-messages');
        
        // Function to close chat
        function closeChat() {
          if (toggleButton && chatWindow) {
            toggleButton.classList.remove('active');
            chatWindow.classList.remove('active');
          }
        }

        // Function to open chat
        function openChat() {
          if (toggleButton && chatWindow) {
            toggleButton.classList.add('active');
            chatWindow.classList.add('active');
          }
        }

        // Toggle chat window
        if (toggleButton && chatWindow) {
          toggleButton.addEventListener('click', function() {
            const isActive = toggleButton.classList.contains('active');
            if (isActive) {
              closeChat();
            } else {
              openChat();
            }
          });
          
          // Auto-open for preview
          setTimeout(() => {
            openChat();
          }, 1000);
        }

        // Header close button
        if (headerCloseButton) {
          headerCloseButton.addEventListener('click', function() {
            closeChat();
          });
        }

        // Reset button
        if (resetButton) {
          resetButton.addEventListener('click', function() {
            messagesContainer.innerHTML = '<div class="ai-message model"><div class="ai-message-content">${escapeForJS(config.welcomeMessage || "Hello! I'm " + (config.botName || "your shopping assistant") + ". I can help you browse products and navigate to checkout. Try asking me to help you checkout!")}</div></div>';
          });
        }
        
        // Handle message sending (demo functionality)
        function sendMessage() {
          const message = chatInput.value.trim();
          if (!message) return;
          
          // Add user message
          addMessage(message, 'user');
          chatInput.value = '';
          
          // Simulate AI response
          setTimeout(() => {
            const lowerMessage = message.toLowerCase();
            
            if (lowerMessage.includes('error') || lowerMessage.includes('problem') || lowerMessage.includes('issue')) {
              addMessage("I\'m sorry, but I encountered an issue while processing your request. Please try again or contact support if the problem persists.", 'model', true);
            } else if (lowerMessage.includes('invalid') || lowerMessage.includes('wrong')) {
              addMessage("The information provided appears to be invalid. Please check your input and try again.", 'model', true);
            } else if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
              addMessage("Hello! I\'m your AI assistant. I\'m here to help you with any questions you might have. Try typing \'error\' to see how error messages are styled!", 'model');
            } else if (lowerMessage.includes('help')) {
              addMessage("I\'m here to assist you! You can ask me questions, and I\'ll do my best to help. I can also demonstrate different message types - try typing \'error\' to see error styling.", 'model');
            } else {
              addMessage("I understand you\'re saying: \'" + message + "\'. I\'m a demo chatbot that showcases different message styles. Try typing \'hello\', \'help\', or \'error\' to see different responses!", 'model');
            }
          }, 1000);
        }
        
        function addMessage(text, sender, isError = false) {
          const messageDiv = document.createElement('div');
          let className = \`ai-message \${sender}\`;
          if (isError) {
            className += ' ai-error-message';
          }
          messageDiv.className = className;
          messageDiv.innerHTML = \`<div class="ai-message-content">\${text}</div>\`;
          messagesContainer.appendChild(messageDiv);
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
        
        // Event listeners
        if (sendButton) {
          sendButton.addEventListener('click', sendMessage);
        }
        
        if (chatInput) {
          chatInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
              sendMessage();
            }
          });
        }
        
        // Initialize the actual SDK with local configuration
        try {
          if (typeof window.SecureLightChatbot !== 'undefined') {
            const bot = new window.SecureLightChatbot({
              clientToken: "mk_242bd59d2cfa2e240dbc3b083e22e3a23c131f1d1e2761bce4192567", 
              appId: "default",
              baseUrl: "` + API_BASE_URL + `",
              encrypted: true,
              debug: true,
              tasks: {
                autoExecute: true,
                confirmation: 'event'
              }
            });
            
            bot.registerTask('order.checkout', async (params, ctx) => {
              navigateToCheckout();
              showNotification('✅ Navigating to checkout page as requested!');
              return { success: true, message: 'Successfully navigated to checkout' };
            }, { aliases: ['sdk.checkout', 'checkout'] });
            
            // Initialize with fallback element
            bot.init('#ai-chatbot-widget');
            
            // Inject custom welcome message after SDK initializes
            const welcomeMessage = '${escapeForJS(config.welcomeMessage || '')}';
            const botName = '${escapeForJS(config.botName || 'Mentoros')}';
            
            // Inject logo and update title in header
            const injectLogoAndTitle = () => {
              const header = document.querySelector('.ai-chat-header');
              if (header) {
                // Always update the h3 text with current botName
                const headerTitle = header.querySelector('.ai-chat-header h3') || header.querySelector('h3');
                if (headerTitle) {
                  headerTitle.textContent = botName + ' Agent';
                }
                
                // Find or create the header-left container
                let headerLeft = header.querySelector('.ai-chat-header-left');
                if (!headerLeft) {
                  // Get the first div (title container) in header
                  const firstDiv = header.querySelector('div');
                  if (firstDiv) {
                    // Wrap existing content in header-left
                    headerLeft = document.createElement('div');
                    headerLeft.className = 'ai-chat-header-left';
                    headerLeft.style.cssText = 'display: flex; align-items: center; gap: 0.625rem; min-width: 0;';
                    
                    // Create logo container
                    const logoDiv = document.createElement('div');
                    logoDiv.className = 'ai-chat-logo';
                    logoDiv.style.cssText = 'width: 40px; height: 40px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;';
                    logoDiv.innerHTML = '<svg viewBox="140 260 320 260" fill="currentColor" style="width: 100%; height: 100%;"><path d="M380.38,386.72h-9.84c-7.82-12.61-19.31-22.95-33.38-29.3-2.49-1.12-5.42-.02-6.54,2.47-1.12,2.49-.02,5.42,2.47,6.54,23.66,10.68,38.94,34.26,38.94,60.07,0,36.31-29.55,65.85-65.87,65.85h-52.38c-36.32,0-65.87-29.54-65.87-65.85s29.55-65.87,65.87-65.87h10.05c2.73,0,4.94-2.21,4.94-4.94s-2.21-4.94-4.94-4.94h-10.05c-27.19,0-51.08,14.4-64.44,35.98h-4.84v-61.9c9.04-2.22,15.77-10.39,15.77-20.11,0-11.41-9.29-20.7-20.71-20.7s-20.7,9.29-20.7,20.7c0,9.72,6.72,17.89,15.75,20.11v62.38c-12.07,2.32-21.22,12.95-21.22,25.69v27.21c0,14.43,11.74,26.18,26.17,26.18h9.79c13.37,21.57,37.25,35.97,64.44,35.97h52.38c20.15,0,39.15-7.9,53.5-22.24,4.2-4.2,7.85-8.81,10.91-13.72h9.8c14.43,0,26.18-11.74,26.18-26.18v-27.21c0-14.43-11.74-26.17-26.18-26.17h0Zm-211.63-82.01c0-5.96,4.85-10.81,10.81-10.81s10.82,4.85,10.82,10.81-4.86,10.82-10.82,10.82c-5.96,0-10.81-4.86-10.81-10.82Zm10.81,151.67c-8.97,0-16.28-7.31-16.28-16.29v-27.21c0-8.97,7.3-16.28,16.28-16.28h4.62c-3.95,9.17-6.15,19.28-6.15,29.89s2.2,20.71,6.15,29.89h-4.63Zm217.11-16.29c0,8.98-7.31,16.29-16.29,16.29h-4.62c4.03-9.33,6.16-19.45,6.16-29.89s-2.19-20.6-6.21-29.89h4.67c8.98,0,16.29,7.3,16.29,16.28v27.21h0Z"/><path d="M238.23,395.03c-10.27,0-18.62,10.07-18.62,22.45s8.35,22.45,18.62,22.45,18.61-10.07,18.61-22.45-8.35-22.45-18.61-22.45h0Zm0,35.01c-4.74,0-8.74-5.75-8.74-12.56s4-12.56,8.74-12.56,8.72,5.75,8.72,12.56-3.99,12.56-8.72,12.56Z"/><path d="M261.97,457.81c-1.93,1.93-1.94,5.06,0,6.99,4.81,4.82,11.21,7.47,18.01,7.47s13.2-2.65,18.01-7.47c1.93-1.93,1.93-5.06,0-6.99-1.93-1.93-5.06-1.93-6.99,0-2.94,2.95-6.85,4.57-11.01,4.57s-8.07-1.62-11.01-4.57c-1.93-1.93-5.06-1.94-6.99,0h0Z"/><path d="M321.72,439.93c10.26,0,18.61-10.07,18.61-22.45s-8.35-22.45-18.61-22.45-18.62,10.07-18.62,22.45c0,12.38,8.36,22.45,18.62,22.45h0Zm0-35.01c4.73,0,8.72,5.75,8.72,12.56s-3.99,12.56-8.72,12.56-8.74-5.75-8.74-12.56,4-12.56,8.74-12.56h0Z"/><path fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="11.34" d="M271.22,359.87h12.7c.21,12.42-2.34,17.67-7.08,27.43,19.16-5.52,30.91-5.77,44.12-27.43h52.64c6.43,0,11.69-5.27,11.69-11.7v-66.14c0-6.42-5.26-11.69-11.69-11.69h-102.39c-6.43,0-11.69,5.27-11.69,11.69v66.66c.28,6.2,5.42,11.19,11.69,11.19h0Z"/></svg>';
                    
                    // Insert logo before the title
                    headerLeft.appendChild(logoDiv);
                    
                    // Move h3 to headerLeft and update text
                    const h3 = firstDiv.querySelector('h3') || header.querySelector('h3');
                    if (h3) {
                      h3.textContent = botName + ' Agent';
                      h3.style.cssText = 'margin: 0; font-size: 1.125rem; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
                      headerLeft.appendChild(h3);
                    }
                    
                    // Replace the first div with headerLeft
                    firstDiv.replaceWith(headerLeft);
                    console.log('[Preview] Logo and title injected');
                  }
                }
              }
            };
            
            // Wait for the SDK to create its message container and inject welcome message
            const injectWelcomeMessage = () => {
              const sdkMessagesContainer = document.querySelector('.ai-chat-messages, [class*="messages"], [class*="chat-body"]');
              
              if (sdkMessagesContainer && welcomeMessage) {
                // Clear any existing messages
                sdkMessagesContainer.innerHTML = '';
                
                // Create welcome message element with proper styling
                const messageDiv = document.createElement('div');
                messageDiv.className = 'ai-message-wrapper ai-bot-message';
                messageDiv.style.cssText = 'display: flex; justify-content: flex-start; margin-bottom: 12px; animation: slideIn 0.3s ease-out;';
                
                const contentDiv = document.createElement('div');
                contentDiv.className = 'ai-message-content';
                contentDiv.style.cssText = \`
                  background-color: var(--ai-chatbot-primary-color, ${config.primaryColor});
                  color: var(--ai-chatbot-ai-message-text, ${config.botMessageTextColor});
                  padding: 10px 14px;
                  border-radius: 12px;
                  max-width: 75%;
                  word-wrap: break-word;
                  font-size: var(--ai-chatbot-font-size, ${config.fontSize});
                  line-height: 1.4;
                \`;
                contentDiv.textContent = welcomeMessage;
                
                messageDiv.appendChild(contentDiv);
                sdkMessagesContainer.appendChild(messageDiv);
                console.log('[Preview] Custom welcome message injected:', welcomeMessage);
              }
            };
            
            // Try to inject immediately after a short delay
            setTimeout(() => {
              injectLogoAndTitle();
              injectWelcomeMessage();
            }, 500);
            
            // Also observe for when chat window is opened
            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' || mutation.addedNodes.length > 0) {
                  const chatWindow = document.querySelector('[class*="chat-window"], .ai-chat-window, [class*="chatbot-container"]');
                  if (chatWindow && chatWindow.classList.contains('active')) {
                    setTimeout(() => {
                      injectLogoAndTitle();
                      injectWelcomeMessage();
                    }, 100);
                  }
                }
              });
            });
            
            observer.observe(document.body, { 
              childList: true, 
              subtree: true, 
              attributes: true,
              attributeFilter: ['class']
            });
          }
        } catch (error) {
          // Using fallback chat interface
        }
      });


    </script>
  </body>
  </html>
`;


const ConfigurationPage: React.FC = () => {
  const [config, setConfig] = useState<SdkConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeSrcDoc, setIframeSrcDoc] = useState('');
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'info', message: string} | null>(null);
  const { setSubscriptionError } = useSubscriptionError();

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const fetchConfig = async () => {
        try {
            setLoading(true);
            const data = await api.get<Partial<SdkConfiguration>>('/api/configuration/sdk');
            const mergedConfig: SdkConfiguration = {
                primaryColor: data?.primaryColor || defaultConfig.primaryColor,
                secondaryColor: data?.secondaryColor || defaultConfig.secondaryColor,
                fontFamily: data?.fontFamily || defaultConfig.fontFamily,
                fontSize: data?.fontSize || defaultConfig.fontSize,
                userMessageTextColor: data?.userMessageTextColor || defaultConfig.userMessageTextColor,
                botMessageTextColor: data?.botMessageTextColor || defaultConfig.botMessageTextColor,
                botName: data?.botName || defaultConfig.botName,
                hoverColor: data?.hoverColor || defaultConfig.hoverColor,
                welcomeMessage: data?.welcomeMessage || defaultConfig.welcomeMessage,
            };
            setConfig(mergedConfig);
            setError(null);
            setSubscriptionError(null); // Clear any previous subscription error
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            setConfig(defaultConfig); // On error, use default config to keep the page functional
        } finally {
            setLoading(false);
        }
    };
    fetchConfig();
  }, []);
  
  // Load background image from localStorage on mount
  useEffect(() => {
    try {
      const savedImage = localStorage.getItem('chatbot-preview-background');
      if (savedImage) {
        setBackgroundImage(savedImage);
      }
    } catch (error) {
      console.error('Failed to load background image from localStorage:', error);
    }
  }, []);
  
  // Effect to generate live chatbot content whenever the config changes
  useEffect(() => {
    if (config) {
      setIframeSrcDoc(getLiveChatbotContent(config));
    }
  }, [config]);

  // Add paste event listener for image upload
  useEffect(() => {
    const pasteHandler = (e: ClipboardEvent) => handlePaste(e);
    window.addEventListener('paste', pasteHandler);
    
    return () => {
      window.removeEventListener('paste', pasteHandler);
    };
  }, []);

  const handleInputChange = <T extends keyof SdkConfiguration>(
    key: T,
    value: SdkConfiguration[T]
  ) => {
    const updatedConfig = { ...config!, [key]: value };
    setConfig(updatedConfig);
    // Live preview updates happen automatically via the useEffect that watches config changes
  };

  const processImageFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showErrorNotification('Please upload an image file (JPG, PNG, GIF, etc.)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showErrorNotification('Image size should be less than 5MB');
      return;
    }
    
    // Convert to base64 for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setBackgroundImage(result);
      
      // Save to localStorage so it persists across sessions
      try {
        localStorage.setItem('chatbot-preview-background', result);
      } catch (error) {
        console.error('Failed to save background image to localStorage:', error);
        // localStorage might be full or disabled, but the image will still work for this session
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const showErrorNotification = (message: string) => {
    setError(message);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setBackgroundImage(null);
    
    // Remove from localStorage
    try {
      localStorage.removeItem('chatbot-preview-background');
    } catch (error) {
      console.error('Failed to remove background image from localStorage:', error);
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          processImageFile(file);
          setNotification({ type: 'success', message: 'Image pasted successfully!' });
        }
        break;
      }
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    try {
        // Save configuration to backend (always happens)
        await api.put('/api/configuration/sdk', config);
        
        // Show success notification
        setNotification({ type: 'success', message: 'Configuration saved successfully!' });
        
        setSubscriptionError(null); // Clear subscription error on successful save
    } catch (err: any) {
        handleApiError(err, setError, setSubscriptionError);
    } finally {
        setSaving(false);
    }
  };
  
  if (loading) {
    return (
        <div className="flex justify-center items-center h-full">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );
  }

  if (!config) {
      return <div className="text-center">No configuration found.</div>
  }

  const notificationStyles = {
    success: 'dark:bg-green-900 bg-green-100 border-green-500 dark:text-green-300 text-green-800',
    info: 'dark:bg-blue-900 bg-blue-100 border-blue-500 dark:text-blue-300 text-blue-800',
  }

  return (
    <div className="space-y-6 sm:space-y-8 px-4 sm:px-0 pb-8 sm:pb-12">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold dark:text-text-primary text-light-text-primary">Widget Configuration</h1>
        <p className="text-sm sm:text-base dark:text-text-secondary text-light-text-secondary mt-1">Customize the appearance of your chat widget.</p>
      </div>

      {/* Desktop notification banner */}
      {notification && (
        <div className={`hidden lg:block p-4 rounded-lg border ${notificationStyles[notification.type]}`}>
            {notification.message}
        </div>
      )}

      {/* Mobile toast notification */}
      {notification && (
        <div className="lg:hidden fixed top-10 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)]">
          <Toast
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
            duration={5000}
          />
        </div>
      )}

      {error && (
          <div className="p-4 rounded-lg border dark:bg-red-900 bg-red-100 border-red-500 dark:text-red-300 text-red-800 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                  <h4 className="font-bold">An error occurred</h4>
                  <p>{error}</p>
              </div>
          </div>
      )}

      <div className="max-w-2xl mx-auto">
        <form className="space-y-6" onSubmit={e => e.preventDefault()}>
            <Card title="Appearance Customization">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Primary Color" type="color" value={config.primaryColor} onChange={e => handleInputChange('primaryColor', e.target.value)} className="h-10 p-1" />
                  <Input label="Secondary Color" type="color" value={config.secondaryColor} onChange={e => handleInputChange('secondaryColor', e.target.value)} className="h-10 p-1" />
                  <Input label="Bot Text Color" type="color" value={config.botMessageTextColor} onChange={e => handleInputChange('botMessageTextColor', e.target.value)} className="h-10 p-1" />
                  <Input label="User Text Color" type="color" value={config.userMessageTextColor} onChange={e => handleInputChange('userMessageTextColor', e.target.value)} className="h-10 p-1" />
                  <Input label="Hover Color" type="color" value={config.hoverColor} onChange={e => handleInputChange('hoverColor', e.target.value)} className="h-10 p-1" />
                  <Input label="Bot Name" value={config.botName} onChange={e => handleInputChange('botName', e.target.value)} placeholder="e.g., Chat Assistant" className="md:col-span-2" />
                  
                  {/* Welcome Message */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium dark:text-text-primary text-light-text-primary mb-2">
                      Welcome Message
                    </label>
                    <textarea
                      value={config.welcomeMessage}
                      onChange={e => handleInputChange('welcomeMessage', e.target.value)}
                      placeholder="e.g., Hello! I am your shopping assistant. How can I help you today?"
                      rows={3}
                      className="w-full px-4 py-2 rounded-lg border dark:border-border-color border-light-border-color dark:bg-surface bg-light-surface dark:text-text-primary text-light-text-primary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    />
                    <p className="text-xs dark:text-gray-400 text-gray-600 mt-1">
                      This is the first message users will see when they open the chatbot.
                    </p>
                  </div>
                  
                  {/* Font Family Selection */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium dark:text-text-primary text-light-text-primary mb-2">
                      Font Family
                    </label>
                    <select
                      value={config.fontFamily}
                      onChange={e => handleInputChange('fontFamily', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border dark:border-border-color border-light-border-color dark:bg-surface bg-light-surface dark:text-text-primary text-light-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="Inter, sans-serif">Inter (Modern Sans-Serif)</option>
                      <option value="Roboto, sans-serif">Roboto (Google Font)</option>
                      <option value="Open Sans, sans-serif">Open Sans (Clean & Readable)</option>
                      <option value="Lato, sans-serif">Lato (Professional)</option>
                      <option value="Montserrat, sans-serif">Montserrat (Geometric)</option>
                      <option value="Poppins, sans-serif">Poppins (Rounded)</option>
                      <option value="Raleway, sans-serif">Raleway (Elegant)</option>
                      <option value="Ubuntu, sans-serif">Ubuntu (Friendly)</option>
                      <option value="Nunito, sans-serif">Nunito (Soft & Rounded)</option>
                      <option value="Arial, sans-serif">Arial (System Font)</option>
                      <option value="Helvetica, Arial, sans-serif">Helvetica (Classic)</option>
                      <option value="Georgia, serif">Georgia (Serif)</option>
                      <option value="Times New Roman, serif">Times New Roman (Traditional)</option>
                      <option value="Dancing Script, cursive">Dancing Script (Cursive)</option>
                      <option value="Pacifico, cursive">Pacifico (Playful Cursive)</option>
                      <option value="Satisfy, cursive">Satisfy (Handwritten)</option>
                      <option value="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">System Default</option>
                    </select>
                    <p className="text-xs dark:text-gray-400 text-gray-600 mt-1">
                      Select a web-safe font. Google Fonts will be loaded automatically for web fonts.
                    </p>
                  </div>

                  {/* Font Size Selection */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium dark:text-text-primary text-light-text-primary mb-2">
                      Font Size
                    </label>
                    <select
                      value={config.fontSize}
                      onChange={e => handleInputChange('fontSize', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border dark:border-border-color border-light-border-color dark:bg-surface bg-light-surface dark:text-text-primary text-light-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="12px">12px (Small)</option>
                      <option value="14px">14px (Default)</option>
                      <option value="16px">16px (Medium)</option>
                      <option value="18px">18px (Large)</option>
                      <option value="20px">20px (Extra Large)</option>
                    </select>
                    <p className="text-xs dark:text-gray-400 text-gray-600 mt-1">
                      Select the base font size for the chatbot text.
                    </p>
                  </div>
              </div>
            </Card>

            <div className="flex flex-col items-center space-y-4">
              <Button onClick={handleSave} isLoading={saving}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
              
              <div className="text-center px-4">
                <p className="text-xs sm:text-sm dark:text-gray-400 text-gray-600">
                  Changes are previewed locally in real-time
                </p>
                <p className="text-xs dark:text-gray-500 text-gray-500 mt-1">
                  Click "Save Configuration" to persist changes to the database
                </p>
              </div>
            </div>
        </form>
      </div>

      <div className="max-w-2xl mx-auto mt-8">
        <Card title="Preview Background (Optional)">
          <div className="space-y-4">
            <p className="text-sm dark:text-gray-400 text-gray-600">
              Upload an image of your website to see how the chatbot will look on your actual site. This is only for preview purposes and won't be saved.
            </p>
            
            <div className="flex flex-col gap-3">
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="relative border-2 border-dashed dark:border-gray-600 border-gray-300 rounded-lg p-4 sm:p-8 text-center hover:border-primary transition-colors"
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="background-image-input"
                />
                <label htmlFor="background-image-input" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 dark:text-gray-500 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-sm sm:text-base font-medium dark:text-gray-300 text-gray-700">
                        {backgroundImage ? 'Change Background Image' : 'Drop your image here or click to browse'}
                      </p>
                      <p className="text-xs sm:text-sm dark:text-gray-500 text-gray-500 mt-1">
                        Supports JPG, PNG, GIF • Max 5MB • You can also paste (Ctrl+V)
                      </p>
                    </div>
                  </div>
                </label>
              </div>
              
              {backgroundImage && (
                <div className="flex items-center justify-between p-2 sm:p-3 dark:bg-gray-800 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs sm:text-sm dark:text-gray-300 text-gray-700">Background image loaded</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 text-xs sm:text-sm font-medium flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-6 sm:mt-8">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-center dark:text-text-primary text-light-text-primary px-4">Live Chatbot Preview</h3>
        <p className="text-xs sm:text-sm dark:text-gray-400 text-gray-600 mb-3 sm:mb-4 text-center px-4">
          {backgroundImage 
            ? 'Preview showing how the chatbot will look on your website' 
            : 'Upload a background image to see how the chatbot will look on your site'
          }
        </p>
        <div 
          className="relative w-full h-[500px] sm:h-[600px] md:h-[700px] lg:h-[800px] rounded-lg sm:rounded-xl border-2 sm:border-4 dark:border-gray-700 border-gray-300 overflow-hidden"
          style={{
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
            <iframe
              key={JSON.stringify(config)}
              srcDoc={iframeSrcDoc}
              title="Live Chatbot Preview"
              className="w-full h-full"
              style={{ border: 'none', background: 'transparent' }}
            />
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;